import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { KeyRound, Scale, ShieldCheck } from 'lucide-react';
import { passkeyLoginStartSchema } from '@cleopatra/contracts';
import { useAuth } from './auth';
import { passkeysSupported } from './passkeys';

export function LoginPage() {
  const { user, loading, login, loginWithPasskey } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'identify' | 'authenticate'>('identify');
  const [error, setError] = useState('');
  const [submittingMethod, setSubmittingMethod] = useState<'password' | 'passkey' | null>(null);
  const routeState = location.state as unknown;

  const requestedPath =
    routeState &&
    typeof routeState === 'object' &&
    'from' in routeState &&
    typeof routeState.from === 'string'
      ? routeState.from
      : '/app';
  const emailIsValid = passkeyLoginStartSchema.safeParse({ email: email.trim() }).success;
  const passkeyAvailable = passkeysSupported();

  if (!loading && user) return <Navigate to={requestedPath} replace />;

  function continueWithEmail(event: FormEvent) {
    event.preventDefault();
    setEmailTouched(true);
    setError('');
    if (!emailIsValid) return;
    setStep('authenticate');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmittingMethod('password');
    setError('');
    try {
      await login({ email: email.trim(), password });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to sign in.');
    } finally {
      setSubmittingMethod(null);
    }
  }

  async function submitPasskey() {
    setSubmittingMethod('passkey');
    setError('');
    try {
      await loginWithPasskey(email.trim());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to sign in with a passkey.');
    } finally {
      setSubmittingMethod(null);
    }
  }

  return (
    <div className="login-page">
      <section className="login-story">
        <Link className="login-brand" to="/" aria-label="Cleopatra public homepage">
          <Scale size={28} />
          <strong>Cleopatra</strong>
        </Link>
        <div className="login-copy">
          <div className="eyebrow text-light">Your work, in focus</div>
          <h1>Every client. Every matter. Every next step.</h1>
          <p>
            Cleopatra keeps the people, deadlines, documents, conversations, and next steps behind
            your legal work connected.
          </p>
          <div className="login-principles">
            <span>
              <ShieldCheck size={18} /> Matter details and activity in one place
            </span>
            <span>
              <ShieldCheck size={18} /> Clear ownership for tasks and deadlines
            </span>
            <span>
              <ShieldCheck size={18} /> Secure access with password or passkey
            </span>
          </div>
        </div>
      </section>
      <section className="login-form-panel">
        <form
          className="login-card"
          onSubmit={(event) =>
            step === 'identify' ? continueWithEmail(event) : void submit(event)
          }
        >
          <Link className="login-home-link" to="/">
            ← Back to home
          </Link>
          <div className="eyebrow">Secure access</div>
          <h2>Welcome back</h2>
          <p className="text-muted">
            {step === 'identify'
              ? 'Enter your email to continue.'
              : 'Choose how you would like to sign in.'}
          </p>
          {error && <div className="alert alert-danger">{error}</div>}
          {step === 'identify' ? (
            <>
              <label className="form-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className={`form-control form-control-lg${emailTouched && !emailIsValid ? ' is-invalid' : ''}`}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={() => setEmailTouched(true)}
                autoComplete="username"
                required
                autoFocus
              />
              {emailTouched && !emailIsValid && (
                <div className="invalid-feedback">Enter a valid email address.</div>
              )}
              <button className="btn btn-primary btn-lg w-100 mt-4">Continue</button>
            </>
          ) : (
            <>
              <div className="login-identity">
                <div>
                  <span>Email</span>
                  <strong>{email.trim()}</strong>
                </div>
                <button
                  className="btn btn-link btn-sm p-0"
                  type="button"
                  disabled={submittingMethod !== null}
                  onClick={() => {
                    setStep('identify');
                    setPassword('');
                    setError('');
                  }}
                >
                  Change
                </button>
              </div>
              {passkeyAvailable && (
                <>
                  <button
                    className="btn btn-primary btn-lg w-100 mt-4 passkey-button"
                    type="button"
                    disabled={submittingMethod !== null}
                    onClick={() => void submitPasskey()}
                  >
                    <KeyRound size={18} aria-hidden="true" />
                    {submittingMethod === 'passkey' ? 'Signing in…' : 'Sign in with a passkey'}
                  </button>
                  <div className="login-method-divider">or use your password</div>
                </>
              )}
              <label className="form-label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                className="form-control form-control-lg"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                autoFocus={!passkeyAvailable}
                required
              />
              <button
                className={`btn btn-lg w-100 mt-4 ${passkeyAvailable ? 'btn-outline-primary' : 'btn-primary'}`}
                disabled={submittingMethod !== null}
              >
                {submittingMethod === 'password' ? 'Signing in…' : 'Sign in with password'}
              </button>
            </>
          )}
          <p className="login-signup-link">
            New to Cleopatra? <Link to="/signup">Create a free workspace</Link>
          </p>
        </form>
      </section>
    </div>
  );
}
